'use client';

import React from 'react';
import styled from 'styled-components';
import { WhiteBadgeStyled } from '@/app/components/search/components/EvolutionCard/Badges/BaseBadge/styled';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useTranslation } from '@/app/components/translations/Translation';
import { HERO_BADGE_TRANSLATION_KEYS } from './heroBadgeKeys';

/** Maximum number of theme badges shown. */
const MAX_HERO_BADGES = 2;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

/**
 * Returns the localized theme badge labels for the hotel, mapped from the static
 * `badges` types through their full translation key and capped to `limit`.
 */
function useHeroThemeBadges(limit: number): string[] {
  const { data: staticData } = useFetchHotelStatic();
  const { t } = useTranslation();

  return (staticData?.badges ?? [])
    .map((type) => HERO_BADGE_TRANSLATION_KEYS[type])
    .filter(Boolean)
    .map((key) => t(key))
    .filter(Boolean)
    .slice(0, limit);
}

type EvoHeroBadgesProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Hotel theme badges for the EVO hero gallery — mirrors the results page card.
 * Shows up to {@link MAX_HERO_BADGES} hotel theme badges. The lastminute deal
 * badge is intentionally not shown here (it lives in the deal-extras section).
 *
 * Positioning is left to the parent (overlay top-left on desktop, below the
 * gallery on mobile) via the forwarded `className`.
 */
export function EvoHeroBadges(props: EvoHeroBadgesProps) {
  const themeBadges = useHeroThemeBadges(MAX_HERO_BADGES);

  if (themeBadges.length === 0) return null;

  return (
    <Container {...props}>
      {themeBadges.map((label) => (
        <WhiteBadgeStyled key={label}>{label}</WhiteBadgeStyled>
      ))}
    </Container>
  );
}
